/**
 * The loading bar's plan: every load step with its share of the bar (weighted by what it really costs, measured with
 * scripts/loadtrace.mjs) and how long it is expected to take. Pure logic, no DOM.
 *
 * The bar is driven by milestones: when a step starts, the bar eases (one CSS transform transition, run by the
 * compositor) from where it is toward most of that step's span over the step's expected time, so it keeps creeping
 * while the main or GPU thread is busy; when the step ends the next one starts from its end. Targets only grow, so
 * the bar never goes backwards.
 */
import type { MessageKey } from '../../i18n';

export type LoadStepId =
  | 'fonts' | 'renderer' | 'level' | 'world' | 'modules' | 'sky' | 'assets' | 'terrain' | 'rider' | 'trees' | 'forest'
  | 'scenery' | 'paddock' | 'compile' | 'post' | 'gpu' | 'branding' | 'warmup' | 'fleet';

/** A step's own progress (0..1), for long steps that know it (pipeline compiles). */
export type StepProgress = (f: number) => void;
/**
 * Runs one timed load step (src/app/loadTracker.ts). `retries` only for idempotent steps (downloads); `optional` for
 * steps the game can do without (pipeline pre-compiles, GPU queue drain): when they fail or stall the load goes on
 * (resolving undefined) instead of stopping.
 */
export interface StepOptions { retries?: number; optional?: boolean }
export type StepRunner = <T>(id: LoadStepId, fn: (progress: StepProgress) => Promise<T> | T, o?: StepOptions) => Promise<T>;
/** No tracking: runs the step as is (dev harness, tests). */
export const runStep: StepRunner = async (_id, fn) => fn(() => undefined);

export interface LoadStepDef {
  id: LoadStepId;
  /** Share of the bar (relative; normalised over the plan). */
  weight: number;
  /** Expected duration on a mid laptop (ms): how long the bar takes to creep across the step. */
  expectMs: number;
  /** Longest this step may take before the load is given up as stalled (ms). */
  timeoutMs: number;
  stage: MessageKey;
}

// Weights and expected times = measured wall time per step (scripts/loadtrace.mjs, RTX laptop, high, 2026-09-29); the bar then moves at an
// even speed over the whole load instead of racing through downloads and sitting still during shader compiles.
export const LOAD_PLAN: readonly LoadStepDef[] = [
  { id: 'fonts', weight: 1, expectMs: 100, timeoutMs: 5000, stage: 'ui.loading.fonts' },
  { id: 'renderer', weight: 5, expectMs: 500, timeoutMs: 50000, stage: 'ui.loading.fonts' },
  { id: 'level', weight: 1, expectMs: 50, timeoutMs: 30000, stage: 'ui.loading.world' },
  { id: 'world', weight: 12, expectMs: 1200, timeoutMs: 30000, stage: 'ui.loading.world' },
  { id: 'modules', weight: 1, expectMs: 60, timeoutMs: 30000, stage: 'ui.loading.world' },
  { id: 'sky', weight: 1, expectMs: 30, timeoutMs: 30000, stage: 'ui.loading.world' },
  { id: 'assets', weight: 4, expectMs: 450, timeoutMs: 60000, stage: 'ui.loading.assets' },
  { id: 'terrain', weight: 1, expectMs: 80, timeoutMs: 30000, stage: 'ui.loading.world' },
  { id: 'rider', weight: 20, expectMs: 2200, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'trees', weight: 1, expectMs: 120, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'forest', weight: 4, expectMs: 400, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'scenery', weight: 1, expectMs: 40, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'paddock', weight: 5, expectMs: 500, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'compile', weight: 110, expectMs: 11000, timeoutMs: 120000, stage: 'ui.loading.shaders' },
  { id: 'post', weight: 120, expectMs: 12000, timeoutMs: 180000, stage: 'ui.loading.shaders' },
  { id: 'gpu', weight: 10, expectMs: 1000, timeoutMs: 30000, stage: 'ui.loading.shaders' },
  { id: 'branding', weight: 1, expectMs: 40, timeoutMs: 30000, stage: 'ui.loading.assets' },
  { id: 'warmup', weight: 14, expectMs: 1400, timeoutMs: 120000, stage: 'ui.loading.shaders' },
  { id: 'fleet', weight: 5, expectMs: 550, timeoutMs: 60000, stage: 'ui.loading.shaders' },
];

export interface Span { start: number; end: number }

/** Each step's [start, end] share of the bar (0..1), in plan order. */
export function spans(plan: readonly LoadStepDef[] = LOAD_PLAN): Map<LoadStepId, Span> {
  const total = plan.reduce((a, s) => a + s.weight, 0);
  const out = new Map<LoadStepId, Span>();
  let at = 0;
  for (const s of plan) { const end = at + s.weight / total; out.set(s.id, { start: at, end }); at = end; }
  return out;
}

/** A bar command: go to `from` now (short ease), then creep toward `to` over `ms` (long ease-out). */
export interface BarMove { from: number; to: number; ms: number; stage: MessageKey }

/** How far into a step the bar may creep before the step reports done (it waits there instead of overshooting). */
export const CREEP_SHARE = 0.85;
/** A step reporting its own progress fills this much of its span before it is done; the creep ahead lasts this long. */
const AT_CAP = 0.97, AT_CREEP_MS = 2500;

/**
 * Monotonic milestones. `start(id)` returns the bar move for a step starting; `finish(id)` the move for a step done
 * (the bar reaches the step's end). A step that started or finished out of order never moves the bar back.
 */
export class LoadBarPlan {
  private readonly spans: Map<LoadStepId, Span>;
  private readonly defs: Map<LoadStepId, LoadStepDef>;
  private reached = 0;
  private target = 0;

  constructor(plan: readonly LoadStepDef[] = LOAD_PLAN) {
    this.spans = spans(plan);
    this.defs = new Map(plan.map((s) => [s.id, s]));
  }

  def(id: LoadStepId): LoadStepDef { return this.defs.get(id)!; }

  start(id: LoadStepId): BarMove {
    const s = this.spans.get(id)!, d = this.defs.get(id)!;
    this.reached = Math.max(this.reached, s.start);
    this.target = Math.max(this.target, this.reached, s.start + (s.end - s.start) * CREEP_SHARE);
    return { from: this.reached, to: this.target, ms: d.expectMs, stage: d.stage };
  }

  finish(id: LoadStepId): BarMove {
    const s = this.spans.get(id)!, d = this.defs.get(id)!;
    this.reached = Math.max(this.reached, s.end);
    this.target = Math.max(this.target, this.reached);
    return { from: this.reached, to: this.target, ms: 0, stage: d.stage };
  }

  /**
   * A long step reporting its own progress `f` (0..1): the bar is at least that far into the step and creeps a little
   * ahead (never past the step's end) until the next report.
   */
  at(id: LoadStepId, f: number): BarMove {
    const s = this.spans.get(id)!, d = this.defs.get(id)!, span = s.end - s.start, cap = s.start + span * AT_CAP;
    this.reached = Math.max(this.reached, s.start + span * AT_CAP * Math.min(1, Math.max(0, f)));
    this.target = Math.max(this.target, this.reached, Math.min(cap, this.reached + span * 0.08));
    return { from: this.reached, to: this.target, ms: AT_CREEP_MS, stage: d.stage };
  }

  /** The fraction the bar is guaranteed to have reached (completed steps). */
  get done(): number { return this.reached; }
}
